import React, { useState, useEffect } from 'react';
import { Instagram, Facebook, MessageCircle, Youtube, Phone, Save, Eye, EyeOff, Link as LinkIcon, ExternalLink, PlayCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchSocialLinks, updateSocialLinks } from '../profileAPIs';

interface SocialLinks {
    instagram: string;
    facebook: string;
    telegram: string;
    youtube: string;
    demoVideo: string;
    whatsapp: string;
}

interface resData {
    socialLinks: {
        success?: boolean;
        data?: SocialLinks;
    }
    message?: string;
}

export default function SocialLinksForm() {
    const [socialLinks, setSocialLinks] = useState<SocialLinks>({
        instagram: '',
        facebook: '',
        telegram: '',
        youtube: '',
        demoVideo: '',
        whatsapp: ''
    });

    const [isLoading, setIsLoading] = useState(false);
    const [showPreview, setShowPreview] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        const loadLinks = async () => {
            setIsLoading(true);
            try {
                const res = await fetchSocialLinks() as resData;
                if (res.socialLinks.success && res.socialLinks.data) {
                    setSocialLinks({
                        instagram: res.socialLinks.data.instagram || "",
                        facebook: res.socialLinks.data.facebook || "",
                        telegram: res.socialLinks.data.telegram || "",
                        youtube: res.socialLinks.data.youtube || "",
                        demoVideo: res.socialLinks.data.demoVideo || "",
                        whatsapp: res.socialLinks.data.whatsapp || ""
                    });
                } else {
                    toast.error(res.message || 'Failed to load social links.');
                }
            } catch (error) {
                toast.error('An error occurred while fetching links.');
                console.error('Error fetching social links:', error);
            } finally {
                setIsLoading(false);
            }
        };
        loadLinks();
    }, []);


    const handleInputChange = (platform: keyof SocialLinks, value: string) => {
        setSocialLinks(prev => ({ ...prev, [platform]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const res = await updateSocialLinks(socialLinks);
            if (res.success) {
                toast.success('Social links updated successfully!');
            } else {
                toast.error(res.message || 'Failed to update social links.');
            }
        } catch (error) {
            toast.error('An error occurred while updating links.');
            console.error('Error updating social links:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const validateUrl = (url: string, platform: string): boolean => {
        if (!url.trim()) return true; // Empty URLs are valid (optional)
        
        try {
            const urlObj = new URL(url);
            switch (platform) {
                case 'instagram':
                    return urlObj.hostname.includes('instagram.com');
                case 'facebook':
                    return urlObj.hostname.includes('facebook.com') || urlObj.hostname.includes('fb.com');
                case 'telegram':
                    return urlObj.hostname.includes('t.me') || urlObj.hostname.includes('telegram.me');
                case 'youtube':
                case 'demoVideo':
                    return urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be');
                case 'whatsapp':
                    return urlObj.hostname.includes('wa.me') || urlObj.hostname.includes('whatsapp.com');
                default:
                    return true;
            }
        } catch {
            return false;
        }
    };

    const getPlaceholderText = (platform: string): string => {
        switch (platform) {
            case 'instagram':
                return 'https://instagram.com/yourusername';
            case 'facebook':
                return 'https://facebook.com/yourpage';
            case 'telegram':
                return 'https://t.me/yourusername';
            case 'youtube':
                return 'https://youtube.com/c/yourchannel';
            case 'demoVideo':
                return 'https://youtube.com/watch?v=...';
            case 'whatsapp':
                return 'https://wa.me/1234567890';
            default:
                return '';
        }
    };

    const togglePreview = (platform: string) => {
        setShowPreview(prev => ({
            ...prev,
            [platform]: !prev[platform]
        }));
    };

    const openLink = (url: string) => {
        if (url && validateUrl(url, '')) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    const socialPlatforms = [
        {
            key: 'instagram' as keyof SocialLinks,
            label: 'Instagram',
            icon: Instagram,
            color: 'text-pink-600',
            bgColor: 'bg-pink-50',
            borderColor: 'border-pink-200'
        },
        {
            key: 'facebook' as keyof SocialLinks,
            label: 'Facebook',
            icon: Facebook,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200'
        },
        {
            key: 'telegram' as keyof SocialLinks,
            label: 'Telegram',
            icon: MessageCircle,
            color: 'text-sky-600',
            bgColor: 'bg-sky-50',
            borderColor: 'border-sky-200'
        },
        {
            key: 'youtube' as keyof SocialLinks,
            label: 'YouTube Channel',
            icon: Youtube,
            color: 'text-red-600',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200'
        },
        {
            key: 'demoVideo' as keyof SocialLinks,
            label: 'Demo Video Link',
            icon: PlayCircle,
            color: 'text-red-600',
            bgColor: 'bg-red-50',
            borderColor: 'border-red-200'
        },
        {
            key: 'whatsapp' as keyof SocialLinks,
            label: 'WhatsApp',
            icon: Phone,
            color: 'text-green-600',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200'
        }
    ];

    return (
        <div className="flex-1">
            <div className="bg-white rounded-2xl shadow-sm p-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Social Links</h2>
                        <p className="text-gray-600 mt-1">Connect your social media profiles (all fields are optional)</p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <LinkIcon size={16} />
                        <span>All links are optional</span>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {socialPlatforms.map((platform) => {
                        const IconComponent = platform.icon;
                        const value = socialLinks[platform.key];
                        const isValid = validateUrl(value, platform.key);
                        const isPreviewVisible = showPreview[platform.key];

                        return (
                            <div key={platform.key} className="space-y-2">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <IconComponent size={16} className={platform.color} />
                                    {platform.label}
                                    <span className="text-xs text-gray-400">(Optional)</span>
                                </label>
                                
                                <div className="relative">
                                    <input
                                        type="url"
                                        value={value}
                                        onChange={(e) => handleInputChange(platform.key, e.target.value)}
                                        placeholder={getPlaceholderText(platform.key)}
                                        className={`w-full px-4 py-3 pr-20 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all ${
                                            !isValid 
                                                ? 'border-red-300 bg-red-50' 
                                                : `border-gray-300 hover:${platform.borderColor}`
                                        }`} />
                                    
                                    {/* Action buttons */}
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                        {value && isValid && (
                                            <button
                                                type="button"
                                                onClick={() => openLink(value)}
                                                className={`p-1 rounded ${platform.bgColor} ${platform.color} hover:bg-opacity-80 transition-colors`}
                                                title="Open link"
                                            >
                                                <ExternalLink size={14} />
                                            </button>
                                        )}
                                        
                                        {value && (
                                            <button
                                                type="button"
                                                onClick={() => togglePreview(platform.key)}
                                                className="p-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                                                title={isPreviewVisible ? "Hide preview" : "Show preview"}
                                            >
                                                {isPreviewVisible ? <EyeOff size={14} /> : <Eye size={14} />}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Validation error */}
                                {value && !isValid && (
                                    <p className="text-red-500 text-sm flex items-center gap-1">
                                        <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                                        Please enter a valid {platform.label} URL
                                    </p>
                                )}

                                {/* URL Preview */}
                                {value && isValid && isPreviewVisible && (
                                    <div className={`p-3 ${platform.bgColor} ${platform.borderColor} border rounded-lg`}>
                                        <div className="flex items-center gap-2 text-sm">
                                            <IconComponent size={14} className={platform.color} />
                                            <span className="font-medium text-gray-700">Preview:</span>
                                            <a 
                                                href={value} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className={`${platform.color} hover:underline truncate`}>
                                                {value}
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Info box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-8">
                        <h3 className="font-medium text-blue-900 mb-2">Social Media Tips:</h3>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• Make sure your profile links are public and accessible</li>
                            <li>• Use complete URLs including https:// for best results</li>
                            <li>• These links will be displayed on your public profile</li>
                            <li>• You can leave any field empty if you don't use that platform</li>
                        </ul>
                    </div>

                    {/* Action buttons */}
                    <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={() => {
                                setSocialLinks({
                                    instagram: '',
                                    facebook: '',
                                    telegram: '',
                                    youtube: '',
                                    demoVideo: '',
                                    whatsapp: ''
                                });
                                toast.success('Form cleared');
                            }}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Clear All
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading || !Object.values(socialLinks).some(link => link.trim() && validateUrl(link, ''))}
                            className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                        >
                            {isLoading ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                <Save size={16} />
                            )}
                            {isLoading ? 'Saving...' : 'Save Social Links'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}